import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';

type SetValue<T> = Dispatch<SetStateAction<T>>;

declare global {
  type LocalStorageEvent = CustomEvent<{ key: string }>;

  interface WindowEventMap {
    'local-storage': LocalStorageEvent;
  }
}

/** https://usehooks-ts.com/react-hook/use-local-storage */
export function useLocalStorage<T>(key: string, initialValue: T): [T, SetValue<T>] {
  const readValue = useCallback((): T => {
    try {
      const item = localStorage.getItem(key);
      return item ? (parseJSON(item, initialValue) as T) : initialValue;
    } catch (error) {
      console.warn(`Error reading localStorage key “${key}”:`, error);
      return initialValue;
    }
  }, [key, initialValue]);

  const [storedValue, setStoredValue] = useState<T>(readValue);

  const setValue: SetValue<T> = useCallback(
    (value) => {
      try {
        const newValue = value instanceof Function ? value(storedValue) : value;
        setStoredValue(newValue);
        setLocalStorageValue(key, newValue);
      } catch (error) {
        console.warn(`Error setting localStorage key “${key}”:`, error);
      }
    },
    [key, storedValue],
  );

  const handleStorageChange = useCallback(
    (event: LocalStorageEvent | StorageEvent) => {
      const eventKey = event instanceof StorageEvent ? event.key : event.detail.key;

      if (eventKey === null) {
        return;
      }

      // if the value of the changed item is undefined, remove it from localStorage.
      const item = localStorage.getItem(eventKey);
      if (item === 'undefined') {
        return localStorage.removeItem(eventKey);
      }

      if (eventKey === key) {
        setStoredValue(readValue());
      }
    },
    [key, readValue],
  );

  useEffect(() => {
    addEventListener('local-storage', handleStorageChange);
    addEventListener('storage', handleStorageChange);
    return () => {
      removeEventListener('local-storage', handleStorageChange);
      removeEventListener('storage', handleStorageChange);
    };
  }, [handleStorageChange]);

  // For the initial render set the localStorage values to the default values.
  useEffect(() => {
    if (!!readValue()) setValue(readValue());
  }, []);

  return [storedValue, setValue];
}

export function setLocalStorageValue(key: string, value: unknown): void {
  localStorage.setItem(key, JSON.stringify(value));
  dispatchEvent(new CustomEvent('local-storage', { detail: { key } }));
}

function parseJSON<T>(value: string | null, initialValue: T): T | undefined {
  try {
    return value === 'undefined' ? undefined : JSON.parse(value ?? '');
  } catch {
    console.log('parsing error on', { value });
    return initialValue;
  }
}
